// @ts-strict-ignore
import React from 'react';
import dayjs from 'dayjs';
import {
  Banner,
  Box,
  Button,
  ButtonVariant,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import type { EntityData, EntityPiiData, MatchData } from '../../types';
import { MatchDetailKeyData } from './MatchDetailKeyData';
import { MatchDetailSummary } from './MatchDetailSummary';
import { MatchDetailFooter, MatchDetailWrapper } from '../../styles';
import { isResolvable } from '../ResolutionModal/helper';

type MatchDetailProps = {
  service: string;
  enableResolution: boolean;
  entity: EntityData;
  entityPii: EntityPiiData;
  handleNext: (() => void) | null;
  handlePrevious: (() => void) | null;
  match: MatchData;
  navigationLabel: string;
  openModal(match: MatchData[]): void;
};

export const MatchDetail = ({
  service,
  enableResolution,
  entity,
  entityPii,
  handleNext,
  handlePrevious,
  match,
  navigationLabel,
  openModal,
}: MatchDetailProps) => (
  <MatchDetailWrapper direction="column" gap={4}>
    {match.resolution.reason && (
      <Banner open borderColor="var(--cl-juicyBlue-400)">
        <Box justifyContent="space-between">
          <Box justifyContent="flex-start">
            <Icon icon={IconName.Approved} size="large" />
            <Text>
              {match.resolution.reason}{match.resolution.note && ` - ${match.resolution.note}`}
            </Text>
          </Box>
          <Box gap={2} justifyContent="flex-end">
            <Text>{match.resolution.agent_full_name}</Text>
            <Text style={{ color: 'var(--cl-gray-700)' }}>
              {match.resolution.timestamp && dayjs(match.resolution.timestamp).format('MMM D, YYYY, H:mm')}
            </Text>
          </Box>
        </Box>
      </Banner>
    )}

    <Box gap={4} alignItems="flex-start">
      <MatchDetailKeyData match={match} entity={entity} entityPii={entityPii} />

      <MatchDetailSummary
        service={service}
        match={match}
        entity={entity}
        entityPii={entityPii}
        height={match.resolution.reason ? 616 : 682}
        isSupportedService={match.is_supported}
      />
    </Box>
    <MatchDetailFooter justifyContent="space-between">
      <Box justifyContent="flex-start" gap={2}>
        <Button
          data-testid="watchlistDetail-previous"
          disabled={handlePrevious === null}
          icon={IconName.CaretLeft}
          onClick={handlePrevious}
          data-dd-action-name="alloydd-watchlistDetail-previous"
        />
        <Button
          data-testid="watchlistDetail-next"
          disabled={handleNext === null}
          icon={IconName.CaretRight}
          onClick={handleNext}
          data-dd-action-name="alloydd-watchlistDetail-next"
        />
        <Text>{navigationLabel}</Text>
      </Box>
      {isResolvable(match) && (
      <Button
        onClick={() => { openModal([match]); }}
        variant={ButtonVariant.primary}
        data-dd-action-name="alloydd-watchlistDetail-resolve"
        disabled={!enableResolution || !!match.resolution.reason}
      >
        Resolve
      </Button>
      )}
    </MatchDetailFooter>
  </MatchDetailWrapper>
);

export default MatchDetail;
